<!--
	/ppf-pattern-software — the paid-search landing page (plan/14 blocker 4).

	A cold visitor from a "ppf pattern software" search lands on the product
	running, not on a brochure: the hero window performs the transform on one
	real job (a Mercedes-AMG GT front bumper, scanned and cleaned) — 3D scan with its cut line, the flattened piece, the exported cut
	path — cycling on its own until the visitor takes the tabs. Then the same
	rhythm as the homepage: the problem, argued; the receipts, dark; the files;
	the presale in plain words; the workflow-demo enquiry (the conversion for
	anyone not ready to pre-order); four FAQs.

	Every figure is read off that session. The export is an unedited draft and
	the page says so, in those words.
-->
<script lang="ts">
	import { resolve } from '$app/paths';
	import { Nav, Footer } from '$lib/components/sections';
	import {
		Button,
		CompareSlider,
		DemoRequest,
		EmailCapture,
		FaqItem,
		Marquee,
		MonoLabel,
		Readout,
		Reveal,
		ScrubWords,
		Section,
		SectionHead
	} from '$lib/components/ui';
	import Meta from '$lib/seo/Meta.svelte';
	import { demo } from '$lib/content/demo';
	import { faqs } from '$lib/content/faq';
	import { pricing, pricingCard, usd } from '$lib/content/pricing';
	import { marquee } from '$lib/content/site';
	import { motionDisabled } from '$lib/motion/reduced';

	const description =
		'PPF pattern software for installers: turn a 3D scan of any car into a measured, editable, plotter-ready cut file. SVG, DXF and PLT. Presale open.';

	/* ── The hero: one bumper, three states, no chrome ─────────────────── */
	const stages = [
		{
			tab: '3D scan',
			asset: 'mbgt-stage-scan',
			w: 1500,
			h: 890,
			alt: 'A scanned Mercedes-AMG GT front bumper rendered in PPF Engine, with the cut line traced along every edge and opening.',
			caption: 'one scan · 429,592 faces · cut line traced on the surface'
		},
		{
			tab: 'Flattened',
			asset: 'mbgt-stage-flat',
			w: 2400,
			h: 1424,
			alt: 'The same bumper flattened into one flat 2D piece, shown true to size at 2571 by 897 millimetres.',
			caption: 'straight from flatten to SVG · unedited, no hand cleanup · 2571 × 897 mm'
		},
		{
			tab: 'Cut path',
			asset: 'mbgt-stage-cut',
			w: 2400,
			h: 1424,
			alt: 'The exported cut file for the bumper: one continuous chartreuse Bézier outline with a scale mark, ready for the plotter.',
			caption: 'exported .svg · 775 Bézier segments · unedited draft'
		}
	] as const;

	let active = $state(0);
	let held = $state(false); // the visitor is on the tabs: stop cycling
	const stage = $derived(stages[active]);

	// The window performs on its own — until someone reaches for the tabs.
	// One interval, cleared on every re-run; nothing keeps running once the
	// visitor has taken over or motion is off.
	$effect(() => {
		if (held || motionDisabled()) return;
		const id = setInterval(() => {
			active = (active + 1) % stages.length;
		}, 3800);
		return () => clearInterval(id);
	});

	/* ── The problem, in three beats ───────────────────────────────────── */
	const argument = [
		'Pattern libraries make ordinary work convenient. They do not help when the model is early, the panel is rare, or the customer wants coverage nobody has drawn.',
		'PPF Engine turns the car in your bay into the source of truth.'
	];
	const problems = [
		{
			index: '01',
			label: 'Unlisted cars',
			value: 'The car is here. The pattern is not.',
			body: 'New model, facelift, rare trim, a bumper with an aftermarket splitter. The library is late or empty, and the job either leaves the shop or gets templated by hand on the car.'
		},
		{
			index: '02',
			label: 'Custom coverage',
			value: 'The catalog decides the seam.',
			body: 'Partial fronts, custom seams and the edge wraps the customer actually asked for are not a download away. You are drawing them on film you already paid for.'
		},
		{
			index: '03',
			label: 'Material risk',
			value: 'The first mistake shows up on the car.',
			body: 'A hand template cannot tell you where the film has to stretch past its limit. You find out with a metre of film on the panel and a heat gun in your hand.'
		}
	] as const;

	/* ── What comes out ────────────────────────────────────────────────── */
	const outputs = [
		{
			ext: '.svg',
			title: 'Editable vector',
			body: 'A clean Bézier cut path in real millimetres, registration marks and a scale line. Opens in Illustrator, Inkscape or any plotter front end that takes SVG.'
		},
		{
			ext: '.dxf',
			title: 'Cutter and CAD software',
			body: 'The same outline for the cutting software you already run, with pieces, cutouts and marks on their own layers.'
		},
		{
			ext: '.plt',
			title: 'Straight to the plotter',
			body: 'HPGL for Graphtec-class cutters, the FC9000 included. Nest the pieces on the roll and send.'
		}
	] as const;

	const faqIds = ['make-own-patterns', 'scanners', 'plotters', 'what'] as const;
	const pageFaqs = faqIds.map((id) => faqs.find((f) => f.id === id)).filter((f) => f != null);
</script>

<Meta title="PPF pattern software" {description} video="amg-gt-bumper" />

<Nav tone="dark" />
<main>
	<!-- ── Hero: the product, running on the bumper ─────────────────────── -->
	<Section as="header" tone="dark" grid class="lp-hero">
		<div
			class="scene"
			role="group"
			aria-label="The bumper, in three states"
			onpointerenter={() => (held = true)}
			onpointerleave={() => (held = false)}
			onfocusin={() => (held = true)}
			onfocusout={() => (held = false)}
		>
			<!-- The part is the scene, not a column: it floats on the same graphite it
			     was rendered on, behind and beside the words, the way the homepage
			     hero puts the copy over the scan. -->
			<div class="stage-stack">
				{#each stages as s, i (s.asset)}
					<img
						class="stage"
						class:stage--on={i === active}
						src="/img/{s.asset}.webp"
						alt={s.alt}
						width={s.w}
						height={s.h}
						fetchpriority={i === 0 ? 'high' : undefined}
						loading={i === 0 ? 'eager' : 'lazy'}
						decoding="async"
						aria-hidden={i === active ? undefined : 'true'}
					/>
				{/each}
			</div>

			<Reveal class="hero__copy">
				<p class="kicker">
					<span class="kicker__tick" aria-hidden="true"></span>
					PPF pattern software · presale open
				</p>
				<h1>{demo.positioning}</h1>
				<p class="lede">
					Scan the car in your bay. Flatten it, check the stretch, fit the curves and send the cut
					file to your plotter. For the vehicles, panels and coverage your pattern database does not
					have.
				</p>
				<div class="actions">
					<Button href="/ppf-pattern-software#demo" size="lg">{demo.cta}</Button>
					<Button href="/#pricing" variant="dark" size="lg">{pricingCard.cta}</Button>
				</div>
				<p class="trust">
					{usd(pricing.founderYear)} first year&nbsp;· no auto-renew&nbsp;· refundable until your access
					begins
				</p>
			</Reveal>
		</div>

		<!-- One rule under the scene: the three states on the left, what you are
		     looking at on the right. The indicator sits on the rule itself. -->
		<div class="stage-bar">
			<div class="tabs" role="tablist" aria-label="Stages of the job">
				{#each stages as s, i (s.tab)}
					<button
						type="button"
						role="tab"
						class="tab"
						class:tab--on={i === active}
						aria-selected={i === active}
						onclick={() => {
							active = i;
							held = true;
						}}
					>
						<span class="tab__index">0{i + 1}</span>
						{s.tab}
					</button>
				{/each}
			</div>
			<p class="stage-cap" aria-live="polite">{stage.caption}</p>
		</div>

		<Reveal class="lp-figures" delay={200}>
			<Readout label="Scan" value="429,592 faces" size="md" accent countUp />
			<Readout label="Template" value="2571 × 897 mm" size="md" />
			<Readout label="p95 stretch" value="8.0%" size="md" accent />
			<Readout label="Cut path" value="775 segments" size="md" countUp />
		</Reveal>
	</Section>

	<Marquee items={marquee} />

	<!-- ── The problem, argued ──────────────────────────────────────────── -->
	<Section id="problem">
		<SectionHead index="01" label="The missing-pattern problem" heading="Any car. Any panel." />
		<div class="argument">
			<ScrubWords text={argument[0]} />
			<ScrubWords text={argument[1]} />
		</div>
		<ul class="ledger">
			{#each problems as item, i (item.label)}
				<Reveal as="li" delay={i * 70}>
					<p class="ledger__index">{item.index}</p>
					<p class="ledger__label">{item.label}</p>
					<h3 class="ledger__title">{item.value}</h3>
					<p class="ledger__body">{item.body}</p>
				</Reveal>
			{/each}
		</ul>
	</Section>

	<!-- ── The receipts ─────────────────────────────────────────────────── -->
	<Section id="proof" tone="dark" grid>
		<SectionHead
			index="02"
			label="One real job"
			heading="A Mercedes-AMG GT front bumper, from scan to cut file"
			intro="The bumper was scanned, cleaned and run through the four steps in PPF Engine. Every number on this page is read off that session. Drag the seam: the scan with its cut line on one side, the exported cut file on the other."
		/>
		<Reveal class="lp-compare" y={20}>
			<CompareSlider
				beforeSrc="/img/mbgt-stage-scan.webp"
				afterSrc="/img/mbgt-stage-cut.webp"
				alt="The same Mercedes-AMG GT front bumper twice: the 3D scan with the cut line traced on it, and the flat chartreuse cut file PPF Engine exported. Drag the seam to compare."
				width={1500}
				height={890}
				beforeLabel="3D scan · cut line"
				afterLabel="2D cut file"
			/>
			<p class="compare__cap">
				front bumper · same job · the flat side is the flatten as exported, unedited · drag the seam
			</p>
		</Reveal>

		<div class="session">
			<Reveal class="lp-shot" y={20}>
				<img
					src="/img/mbgt-drawing.webp"
					alt="The exported cut file drawn as a dimensioned sheet: the bumper outline in white on black, 2571 millimetres wide and 897 millimetres tall, with a scale mark."
					width={2400}
					height={1100}
					loading="lazy"
					decoding="async"
				/>
			</Reveal>
			<Reveal class="lp-session-copy" delay={80}>
				<MonoLabel>The file, as exported</MonoLabel>
				<h3>Straight out of the app, unedited.</h3>
				<p>
					The cleaned scan was flattened in PPF Engine and exported straight to SVG: one piece, 2571
					× 897 mm, 775 Bézier segments. No anchor was moved, nothing was cleaned up by hand. The
					file is a draft, not a production pattern, and every number on this page comes from this
					one session.
				</p>
				<dl class="session__rows">
					<div>
						<dt>Fit</dt>
						<dd>775 segments · ≤ 0.30 mm target</dd>
					</div>
					<div>
						<dt>Relief</dt>
						<dd>2 headlight slits · 47.7 / 47.8 mm</dd>
					</div>
					<div>
						<dt>Coverage</dt>
						<dd>99.9% of the scanned surface</dd>
					</div>
				</dl>
			</Reveal>
		</div>
	</Section>

	<!-- ── What comes out ───────────────────────────────────────────────── -->
	<Section id="outputs" tone="panel">
		<SectionHead
			index="03"
			label="What comes out"
			heading="A file your plotter already understands."
			intro="No proprietary format. The scan and every decision stay editable in PPF Engine; the export is a plain vector file in millimetres."
		/>
		<ul class="ledger ledger--files">
			{#each outputs as out, i (out.ext)}
				<Reveal as="li" delay={i * 70}>
					<p class="ledger__index ledger__index--ext">{out.ext}</p>
					<h3 class="ledger__title">{out.title}</h3>
					<p class="ledger__body">{out.body}</p>
				</Reveal>
			{/each}
		</ul>
	</Section>

	<!-- ── The presale, plainly ─────────────────────────────────────────── -->
	<Section id="presale">
		<SectionHead
			index="04"
			label="The presale, plainly"
			heading="What you are buying, and what you are not."
			intro="A prepaid first year at less than half the public price, starting the day we deliver. Not a donation, not a crowdfunding pledge, and not a subscription that renews on its own."
		/>
		<Reveal class="lp-offer" y={20}>
			<div class="offer__price">
				<p class="offer__eyebrow">{pricingCard.eyebrow}</p>
				<p class="offer__amount">
					<span class="offer__num">{pricingCard.price}</span>
					<span class="offer__suffix">{pricingCard.priceSuffix}</span>
				</p>
				<p class="offer__anchor">
					<span class="offer__anchor-label">{pricingCard.nextPriceLabel}</span>
					<span class="offer__anchor-num">{pricingCard.nextPrice}{pricingCard.nextPriceSuffix}</span
					>
				</p>
				<p class="offer__delivery">
					Delivery estimated {pricing.deliveryWindow}. Your 12 months start then.
				</p>
			</div>
			<div class="offer__terms">
				<ul>
					{#each pricingCard.bullets as bullet (bullet)}
						<li>{bullet}</li>
					{/each}
				</ul>
				<p class="offer__refund">{pricingCard.refundLine}</p>
				<div class="actions">
					<Button href="/#pricing" size="lg">{pricingCard.cta}</Button>
					<Button href="/legal/preorder" variant="ghost" size="lg">Read the pre-order terms</Button>
				</div>
			</div>
		</Reveal>
	</Section>

	<!-- ── Workflow demo: the step before the presale ───────────────────── -->
	<Section id="demo" tone="dark">
		<div class="lead__grid">
			<Reveal class="lp-lead-copy">
				<MonoLabel>{demo.label}</MonoLabel>
				<h2>{demo.heading}</h2>
				<p>{demo.intro}</p>
				<ol class="lead__steps">
					{#each demo.steps as step (step)}
						<li>{step}</li>
					{/each}
				</ol>
			</Reveal>
			<Reveal class="lp-lead-form" delay={80}>
				<DemoRequest source="landing" />
				<div class="lead__note">
					<p>
						Only want the launch note? One email when it ships, one when presale licenses run out.
					</p>
					<EmailCapture source="landing" cta="Send me the launch note" />
				</div>
			</Reveal>
		</div>
	</Section>

	<!-- ── Four questions ───────────────────────────────────────────────── -->
	<Section id="faq">
		<div class="faq">
			<div class="faq__side">
				<SectionHead
					index="05"
					label="Four questions installers ask first"
					heading="Straight answers."
				/>
				<p class="faq__more">
					The full list is on the <a href={resolve('/faq')}>FAQ page</a>. Equipment questions:
					<a href={resolve('/technology/scanner-plotter-compatibility')}
						>scanner and plotter compatibility</a
					>.
				</p>
			</div>
			<div class="faq__list">
				{#each pageFaqs as item (item.id)}
					<FaqItem question={item.q} level={3}>
						<p>{item.a}</p>
					</FaqItem>
				{/each}
			</div>
		</div>
	</Section>
</main>
<Footer />

<style>
	/* ── Hero ───────────────────────────────────────────────────────────── */
	:global(.lp-hero) {
		min-height: 100svh;
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding-block: clamp(5.5rem, 12vh, 8rem) clamp(2.5rem, 5vh, 3.5rem);
	}
	.scene {
		position: relative;
		display: flex;
		align-items: center;
		min-height: clamp(26rem, 58vh, 40rem);
	}
	.scene :global(.hero__copy) {
		position: relative;
		z-index: 1;
		max-width: 34rem;
	}
	/* The stages sit stacked on the right two thirds and crossfade; the active
	   one is the only one in the accessibility tree. A short shade on the left
	   edge keeps the copy legible where the two overlap on narrow desktops. */
	.stage-stack {
		position: absolute;
		inset: 0 -2% 0 40%;
		animation: stage-in 900ms var(--ease-out) both;
	}
	.stage-stack::before {
		content: '';
		position: absolute;
		inset: 0;
		z-index: 1;
		background: linear-gradient(90deg, var(--color-app-950) 0%, transparent 36%);
		pointer-events: none;
	}
	@keyframes stage-in {
		from {
			opacity: 0;
			transform: translateX(12px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}
	.stage {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: contain;
		object-position: center;
		opacity: 0;
		transform: scale(0.985);
		transition:
			opacity 800ms var(--ease-out),
			transform 1600ms var(--ease-out);
	}
	.stage--on {
		opacity: 1;
		transform: none;
	}
	@media (max-width: 1023px) {
		.scene {
			flex-direction: column;
			align-items: stretch;
			gap: 2rem;
			min-height: 0;
		}
		.stage-stack {
			position: relative;
			inset: auto;
			aspect-ratio: 1500 / 890;
			order: -1;
		}
		.stage-stack::before {
			display: none;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.stage-stack {
			animation: none;
		}
		.stage {
			transition: none;
		}
	}
	.kicker {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.kicker__tick {
		width: 0.5rem;
		height: 0.5rem;
		border-radius: 999px;
		background: var(--color-cut-400);
		box-shadow: 0 0 0 4px color-mix(in srgb, var(--color-cut-400) 22%, transparent);
	}
	h1 {
		margin-top: 1.25rem;
		max-width: 13ch;
		font-size: clamp(2.5rem, 5.4vw, 4.75rem);
		line-height: var(--text-display--line-height);
		letter-spacing: var(--text-display--letter-spacing);
		font-weight: var(--text-display--font-weight);
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	.lede {
		margin-top: 1.75rem;
		max-width: 42ch;
		font-size: var(--text-lead);
		line-height: var(--text-lead--line-height);
		letter-spacing: var(--text-lead--letter-spacing);
		color: var(--color-bone-500);
		text-wrap: pretty;
	}
	.actions {
		display: flex;
		flex-wrap: wrap;
		gap: 0.75rem;
		margin-top: 2rem;
	}
	.trust {
		margin-top: 1.25rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		line-height: 1.8;
		color: var(--color-bone-500);
		max-width: 60ch;
		text-wrap: pretty;
	}

	/* One hairline; the active tab's indicator sits on it. */
	.stage-bar {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		justify-content: space-between;
		gap: 0.75rem 1.5rem;
		margin-top: clamp(1.5rem, 4vh, 3rem);
		padding-top: 1rem;
		border-top: 1px solid var(--color-app-700);
	}
	.tabs {
		display: flex;
		gap: 1.5rem;
	}
	.tab {
		position: relative;
		display: inline-flex;
		align-items: baseline;
		gap: 0.45rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-500);
		transition: color var(--dur-fast) var(--ease-out);
	}
	.tab::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		top: calc(-1rem - 1px);
		height: 2px;
		background: var(--color-cut-400);
		transform: scaleX(0);
		transform-origin: left;
		transition: transform 320ms var(--ease-out);
	}
	.tab:hover {
		color: var(--color-bone-100);
	}
	.tab--on {
		color: var(--color-cut-400);
	}
	.tab--on::after {
		transform: scaleX(1);
	}
	.tab__index {
		font-size: var(--text-mono-xs);
		opacity: 0.55;
	}
	.stage-cap {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.04em;
		color: var(--color-bone-500);
		text-wrap: pretty;
	}
	:global(.lp-figures) {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 1.5rem 2rem;
		margin-top: clamp(2rem, 4vh, 3rem);
		padding-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	@media (min-width: 800px) {
		:global(.lp-figures) {
			grid-template-columns: repeat(4, minmax(0, 1fr));
		}
	}

	/* ── The argument ───────────────────────────────────────────────────── */
	.argument {
		display: flex;
		flex-direction: column;
		gap: 1.5em;
		margin-top: clamp(2.5rem, 4vw, 3.5rem);
		max-width: 34ch;
		font-size: clamp(1.375rem, 2.6vw, 2rem);
		font-weight: 500;
		line-height: 1.35;
		letter-spacing: -0.02em;
	}
	@media (min-width: 800px) {
		.argument :global(.scrub-words + .scrub-words) {
			margin-left: clamp(0rem, 8vw, 7rem);
		}
	}
	/* The ledger: no boxes, no icons. A rule, an index, a label, a claim. */
	.ledger {
		list-style: none;
		display: grid;
		gap: 2rem clamp(1.5rem, 3vw, 3rem);
		margin-top: clamp(3rem, 5vw, 4rem);
	}
	@media (min-width: 800px) {
		.ledger {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}
	.ledger :global(li) {
		padding-top: 1.25rem;
		border-top: 1px solid var(--color-line-strong);
	}
	.ledger__index {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		color: var(--color-cut-700);
	}
	.ledger__index--ext {
		font-size: 1.5rem;
		letter-spacing: -0.01em;
	}
	.ledger__label {
		margin-top: 2.5rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.ledger__title {
		margin-top: 0.5rem;
		font-family: var(--font-display);
		font-size: 1.5rem;
		font-weight: 600;
		letter-spacing: -0.02em;
		line-height: 1.15;
		color: var(--color-ink-900);
		text-wrap: balance;
	}
	.ledger--files .ledger__title {
		margin-top: 1.5rem;
	}
	.ledger__body {
		margin-top: 0.875rem;
		color: var(--color-ink-500);
		text-wrap: pretty;
	}

	/* ── Proof ──────────────────────────────────────────────────────────── */
	:global(.lp-compare) {
		margin-top: clamp(2.5rem, 5vw, 4rem);
	}
	.compare__cap {
		margin-top: 0.875rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		color: var(--color-bone-500);
	}
	.session {
		display: grid;
		gap: clamp(1.5rem, 4vw, 3rem);
		margin-top: clamp(3rem, 6vw, 5rem);
		align-items: center;
	}
	@media (min-width: 900px) {
		.session {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
		}
	}
	:global(.lp-shot) img {
		display: block;
		width: 100%;
		height: auto;
		border-radius: var(--radius-win);
		border: 1px solid var(--color-app-700);
		box-shadow: var(--shadow-card);
	}
	:global(.lp-session-copy) h3 {
		margin-top: 0.75rem;
		font-size: var(--text-h3);
		line-height: var(--text-h3--line-height);
		letter-spacing: var(--text-h3--letter-spacing);
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	:global(.lp-session-copy) p {
		margin-top: 1rem;
		color: var(--color-bone-500);
		text-wrap: pretty;
	}
	.session__rows {
		margin-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	.session__rows div {
		display: grid;
		grid-template-columns: 6rem 1fr;
		gap: 1rem;
		padding-block: 0.6rem;
		border-bottom: 1px solid var(--color-app-700);
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
	}
	.session__rows dt {
		color: var(--color-bone-500);
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}
	.session__rows dd {
		color: var(--color-bone-100);
	}

	/* ── The offer ──────────────────────────────────────────────────────── */
	:global(.lp-offer) {
		display: grid;
		gap: clamp(2rem, 5vw, 4rem);
		margin-top: clamp(2.5rem, 5vw, 4rem);
		padding: clamp(1.75rem, 4vw, 3rem);
		border-radius: var(--radius-win);
		border: 1px solid var(--color-line-strong);
		background: var(--color-surface);
		box-shadow: var(--shadow-card);
	}
	@media (min-width: 900px) {
		:global(.lp-offer) {
			grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			align-items: start;
		}
	}
	.offer__eyebrow {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.offer__amount {
		margin-top: 0.75rem;
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: 0.25rem 0.75rem;
	}
	.offer__num {
		font-family: var(--font-display);
		font-size: clamp(3rem, 6vw, 4.75rem);
		font-weight: 600;
		letter-spacing: -0.035em;
		line-height: 1;
		color: var(--color-cut-700);
		font-variant-numeric: tabular-nums;
	}
	.offer__suffix {
		font-size: var(--text-lead);
		color: var(--color-ink-700);
	}
	.offer__anchor {
		margin-top: 1rem;
		display: flex;
		flex-wrap: wrap;
		gap: 0.25rem 0.75rem;
		align-items: baseline;
	}
	.offer__anchor-label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.offer__anchor-num {
		font-family: var(--font-display);
		font-size: var(--text-h3);
		font-weight: 600;
		color: var(--color-ink-300);
		text-decoration: line-through;
		text-decoration-thickness: 1.5px;
	}
	.offer__delivery {
		margin-top: 1.25rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		line-height: 1.6;
		color: var(--color-ink-400);
		max-width: 30ch;
		text-wrap: pretty;
	}
	.offer__terms ul {
		list-style: none;
	}
	.offer__terms li {
		position: relative;
		padding: 0.7rem 0 0.7rem 1.5rem;
		border-bottom: 1px solid var(--color-line);
		color: var(--color-ink-700);
	}
	.offer__terms li::before {
		content: '';
		position: absolute;
		left: 0;
		top: 1.2rem;
		width: 0.6rem;
		height: 0.6rem;
		border-radius: 2px;
		background: var(--color-cut-400);
	}
	.offer__refund {
		margin-top: 1.25rem;
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		line-height: 1.6;
		color: var(--color-ink-400);
		text-wrap: pretty;
	}
	.offer__terms .actions {
		margin-top: 1.5rem;
	}

	/* ── Workflow demo ──────────────────────────────────────────────────── */
	.lead__grid {
		display: grid;
		gap: clamp(2rem, 6vw, 5rem);
		align-items: start;
	}
	@media (min-width: 900px) {
		.lead__grid {
			grid-template-columns: minmax(0, 6fr) minmax(0, 6fr);
		}
	}
	:global(.lp-lead-copy) h2 {
		margin-top: 0.75rem;
		font-size: var(--text-h2);
		line-height: var(--text-h2--line-height);
		letter-spacing: var(--text-h2--letter-spacing);
		color: var(--color-bone-100);
		text-wrap: balance;
	}
	:global(.lp-lead-copy) p {
		margin-top: 1.25rem;
		max-width: 44ch;
		color: var(--color-bone-500);
		text-wrap: pretty;
	}

	.lead__steps {
		margin-top: 1.5rem;
		padding-left: 1.25rem;
		max-width: 48ch;
		display: grid;
		gap: 0.75rem;
		list-style: decimal;
		color: var(--color-bone-300);
	}
	.lead__note {
		margin-top: 2.5rem;
		padding-top: 1.5rem;
		border-top: 1px solid var(--color-app-700);
	}
	.lead__note p {
		margin-bottom: 1rem;
		color: var(--color-bone-500);
	}

	/* ── FAQ ────────────────────────────────────────────────────────────── */
	.faq {
		display: grid;
		gap: clamp(2rem, 4vw, 3rem);
		align-items: start;
	}
	@media (min-width: 900px) {
		.faq {
			grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
		}
	}
	.faq__more {
		margin-top: 1.25rem;
		color: var(--color-ink-400);
	}
	.faq__more a {
		text-decoration: underline;
		text-underline-offset: 0.2em;
		color: var(--color-ink-700);
	}
	.faq__list {
		border-top: 1px solid var(--color-line);
	}
	.faq__list p {
		max-width: var(--container-measure);
		padding-bottom: 1.25rem;
	}
</style>
